import React, { useState } from 'react';
import {
  TableRow,
  TableCell,
  Checkbox,
  Typography,
  Stack,
  Chip,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  CheckCircle as CheckIcon,
  RadioButtonUnchecked as UncheckedIcon,
  TrendingUp as CreditIcon,
  TrendingDown as DebitIcon,
  LinkOff as UnlinkIcon,
  Add as AddIcon,
} from '@mui/icons-material';
import { formatCurrency } from '../../helpers/formatCurrency';
import { isReconciled } from '../../helpers/reconciliationHelpers';
import UnreconcileConfirmDialog from './UnreconcileConfirmDialog';

// Helper function to determine if a transaction is a transfer
const isTransferTransaction = (transaction) => {
  return transaction.journalEntryType === 5;
};

const TransactionRow = ({
  transaction,
  isSelected,
  onToggle,
  formatDate,
  onUnreconcile,
  onCreateEntry,
  onCreateEntriesBulk,
}) => {
  const isReconciledStatus = isReconciled(transaction);
  const [showUnreconcileDialog, setShowUnreconcileDialog] = useState(false);

  const handleUnreconcileClick = (e) => {
    e.stopPropagation(); // Prevent row selection
    setShowUnreconcileDialog(true);
  };

  const handleCreateEntryClick = (e) => {
    e.stopPropagation(); // Prevent row selection
    if (onCreateEntry) {
      onCreateEntry(transaction);
    }
  };

  const handleConfirmUnreconcile = async () => {
    if (onUnreconcile && transaction.reconciliationData?.reconciliationId) {
      await onUnreconcile(transaction.reconciliationData.reconciliationId, transaction);
      // Data refresh is handled automatically by the parent component via refreshAllData
    }
    setShowUnreconcileDialog(false);
  };

  return (
    <>
      <TableRow
        selected={isSelected}
        sx={{
          '&:hover': { backgroundColor: 'action.hover' },
          opacity: isReconciledStatus ? 0.6 : 1,
          backgroundColor: isSelected ? 'rgba(25, 118, 210, 0.08)' : 'inherit',
          '&.Mui-selected': {
            backgroundColor: 'rgba(25, 118, 210, 0.12)',
            '&:hover': {
              backgroundColor: 'rgba(25, 118, 210, 0.16)',
            },
          },
          height: 48, // More compact height
          cursor: 'pointer',
        }}
        onClick={() => !isReconciledStatus && onToggle(transaction)}
      >
        <TableCell padding="checkbox">
          <Checkbox
            checked={isSelected}
            onClick={(e) => {
              e.stopPropagation();
              if (!isReconciledStatus) {
                onToggle(transaction);
              }
            }}
            disabled={isReconciledStatus}
            size="small"
          />
        </TableCell>

        <TableCell
          sx={{
            width: 80,
            maxWidth: 80,
            padding: '8px 4px',
            textAlign: 'center',
          }}
        >
          <Typography variant="body2" sx={{ fontSize: '0.75rem', fontWeight: 500 }}>
            {formatDate(transaction.datePosted)}
          </Typography>
        </TableCell>

        <TableCell>
          <Tooltip title={transaction.description} placement="top">
            <Typography variant="body2" noWrap sx={{ fontSize: '0.8rem', maxWidth: 180 }}>
              {transaction.description}
            </Typography>
          </Tooltip>
          {transaction.checkNumber && (
            <Typography variant="caption" color="text.secondary" display="block">
              Cheque: {transaction.checkNumber}
            </Typography>
          )}
        </TableCell>

        <TableCell align="right">
          <Stack alignItems="flex-end" spacing={0.5}>
            <Typography
              variant="body2"
              fontWeight="600"
              sx={{
                fontSize: '0.9rem',
                color: isTransferTransaction(transaction)
                  ? transaction.amount > 0
                    ? '#4caf50'
                    : '#f44336' // Tom mais suave para transferências
                  : transaction.amount > 0
                  ? 'success.main'
                  : 'error.main',
              }}
            >
              {formatCurrency(Math.abs(transaction.amount || 0))}
            </Typography>
            <Chip
              icon={
                isTransferTransaction(transaction) ? null : transaction.amount > 0 ? (
                  <CreditIcon />
                ) : (
                  <DebitIcon />
                )
              }
              label={
                isTransferTransaction(transaction)
                  ? 'Transferência'
                  : transaction.amount > 0
                  ? 'Entrada'
                  : 'Saída'
              }
              size="small"
              color={
                isTransferTransaction(transaction)
                  ? transaction.amount > 0
                    ? 'success'
                    : 'error' // Mantém success/error mas com tom diferente
                  : transaction.amount > 0
                  ? 'success'
                  : 'error'
              }
              sx={{
                height: 16,
                fontSize: '0.6rem',
                '& .MuiChip-icon': { fontSize: '0.7rem' },
                ...(isTransferTransaction(transaction) && {
                  backgroundColor: transaction.amount > 0 ? '#e8f5e8' : '#ffebee', // Tons mais suaves
                  color: transaction.amount > 0 ? '#2e7d32' : '#c62828', // Verde e vermelho mais escuros
                  '&:hover': {
                    backgroundColor: transaction.amount > 0 ? '#c8e6c9' : '#ffcdd2',
                  },
                }),
              }}
            />
          </Stack>
        </TableCell>

        <TableCell>
          {isReconciledStatus ? (
            <Stack direction="row" alignItems="center" spacing={1}>
              <Chip
                icon={<CheckIcon />}
                label="Conciliado"
                color="success"
                size="small"
                sx={{ fontSize: '0.65rem', height: 20 }}
              />
              {onUnreconcile && (
                <Tooltip title="Desconciliar transação" placement="top">
                  <IconButton
                    size="small"
                    onClick={handleUnreconcileClick}
                    sx={{
                      color: 'warning.main',
                      '&:hover': {
                        backgroundColor: 'warning.light',
                        color: 'warning.dark',
                      },
                      padding: '4px',
                    }}
                  >
                    <UnlinkIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
            </Stack>
          ) : (
            <Stack direction="row" alignItems="center" spacing={1}>
              <Chip
                icon={<UncheckedIcon />}
                label="Pendente"
                color="default"
                size="small"
                sx={{ fontSize: '0.65rem', height: 20 }}
              />
              {onCreateEntry && (
                <Tooltip title="Criar lançamento correspondente no Vita" placement="top">
                  <IconButton
                    size="small"
                    onClick={handleCreateEntryClick}
                    sx={{
                      color: 'primary.main',
                      '&:hover': {
                        backgroundColor: 'primary.light',
                        color: 'primary.dark',
                      },
                      padding: '4px',
                    }}
                  >
                    <AddIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
            </Stack>
          )}
        </TableCell>
      </TableRow>

      {/* Dialog de Confirmação de Desconciliação */}
      <UnreconcileConfirmDialog
        open={showUnreconcileDialog}
        onClose={() => setShowUnreconcileDialog(false)}
        onConfirm={handleConfirmUnreconcile}
        reconciliationData={transaction.reconciliationData}
        item={transaction}
      />
    </>
  );
};

export default TransactionRow;
